import React, { useState } from "react";
import useNotifications from "./useNotifications";
import CustomLoading from "../../../utils/LoadingIndicator/CustomLoading";

const Notifications = () => {
  const {
    notifications,
    unreadCount,
    hasNotifications,
    isLoading,
    isMarkingAsRead,
    isMarkingAllAsRead,
    isDeleting,
    isDeletingAll,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    refetch,
  } = useNotifications();

  const [selectedNotifications, setSelectedNotifications] = useState([]);

  const handleSelectAll = () => {
    if (selectedNotifications.length === notifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications.map((n) => n._id));
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleDeleteSelected = () => {
    if (selectedNotifications.length === 0) return;

    if (selectedNotifications.length === notifications.length) {
      deleteAllNotifications();
      setSelectedNotifications([]);
    } else {
      selectedNotifications.forEach((id) => deleteNotification(id));
      setSelectedNotifications([]);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "order":
        return "bg-blue-100 text-blue-800";
      case "system":
        return "bg-gray-100 text-gray-800";
      case "promotion":
        return "bg-purple-100 text-purple-800";
      case "security":
        return "bg-red-100 text-red-800";
      default:
        return "bg-green-100 text-green-800";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "order":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            viewBox="0 0 16 16"
            fill="currentColor"
            className="text-blue-600"
          >
            <path
              fillRule="evenodd"
              d="M4 2a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2h.11a1 1 0 0 1 .97.757l.25 1A1 1 0 0 1 12.36 7H12v.5h.5a1 1 0 0 1 1 1V9a1 1 0 0 1-1 1H12v2a1 1 0 0 1-1 1H8.707l-.296.296l-.826 1.651a1 1 0 0 1-.894.553h-3.04a1 1 0 0 1-.554-.168l-1.152-.768a1 1 0 0 1-.445-.832v-3.696a1 1 0 0 1 .01-.142l.934-6.535a1 1 0 0 1 .99-.859H4zm0 1.5h-.566L2.5 10.036v3.696l1.151.768H4.5v-1.333a1 1 0 0 1 .2-.6l2.64-3.52l-.84-.84l-2.146 2.147l-.708-.708L4 9.293zm1 4.793l.793-.793a1 1 0 0 1 1.414 0l.84.84a1 1 0 0 1 .093 1.307L6.375 12H11V2H5zM12 6h.36l-.25-1H12zm0 2.5V9h.5v-.5zM5.625 13l-.125.167V14.5h1.191l.75-1.5zM7.5 3.5V3h1v.5zm.5 0A1.5 1.5 0 0 0 6.5 5H6v1h4V5h-.5A1.5 1.5 0 0 0 8 3.5m0 1a.5.5 0 0 0-.5.5h1a.5.5 0 0 0-.5-.5M6.146 9.854l-.5-.5l.708-.708l.5.5z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "system":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-600"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
        );
      case "promotion":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-purple-600"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
          </svg>
        );
      case "security":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-red-600"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
        );
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-green-600"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <CustomLoading size="2rem" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
          <p className="text-gray-600 mt-1">
            {unreadCount > 0
              ? `${unreadCount} unread notification${
                  unreadCount > 1 ? "s" : ""
                }`
              : "All caught up!"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refetch}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="inline-block mr-2"
            >
              <path d="M21 2v6h-6"></path>
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
              <path d="M3 22v-6h6"></path>
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Actions Bar */}
      {hasNotifications && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={
                    selectedNotifications.length === notifications.length
                  }
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className="ml-2 text-sm text-gray-700">Select All</span>
              </label>
              {selectedNotifications.length > 0 && (
                <span className="text-sm text-gray-500">
                  {selectedNotifications.length} selected
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={isMarkingAllAsRead}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isMarkingAllAsRead ? "Marking..." : "Mark All as Read"}
                </button>
              )}
              {selectedNotifications.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  disabled={isDeleting || isDeletingAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {isDeleting || isDeletingAll
                    ? "Deleting..."
                    : "Delete Selected"}
                </button>
              )}
              <button
                onClick={deleteAllNotifications}
                disabled={isDeletingAll}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isDeletingAll ? "Deleting..." : "Clear All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications List */}
      {hasNotifications ? (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification._id}
              className={`bg-white border rounded-lg p-4 transition-all duration-200 ${
                notification.read
                  ? "border-gray-200 opacity-75"
                  : "border-red-200 bg-red-50"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.includes(notification._id)}
                    onChange={() => handleSelectNotification(notification._id)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500 mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getCategoryIcon(notification.category)}
                      <h3 className="text-lg font-semibold text-gray-900">
                        {notification.title}
                      </h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(
                          notification.category
                        )}`}
                      >
                        {notification.category}
                      </span>
                      {!notification.read && (
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      )}
                    </div>
                    <p className="text-gray-700 mb-3">{notification.body}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {new Date(notification.createdAt).toLocaleString()}
                      </span>
                      <div className="flex items-center gap-2">
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification._id)}
                            disabled={isMarkingAsRead}
                            className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            Mark as Read
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification._id)}
                          disabled={isDeleting}
                          className="px-3 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={32}
              height={32}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No notifications yet
          </h3>
          <p className="text-gray-500">
            You're all caught up! New notifications will appear here.
          </p>
        </div>
      )}
    </div>
  );
};

export default Notifications;
